import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FaArrowLeft,
  FaBook,
  FaCalendarAlt,
  FaCheckCircle,
  FaEdit,
  FaFilter,
  FaGraduationCap,
  FaLayerGroup,
  FaPlus,
  FaSave,
  FaSearch,
  FaSpinner,
  FaStethoscope,
  FaTrash,
  FaUserTie,
  FaUsers,
  FaTimes,
  FaClock,
  FaExclamationTriangle
} from 'react-icons/fa';
import { getCurrentUserRole } from '../utils/auth';
import { normalizeUsers, normalizeArray } from '../utils/userUtils';
import { getApiUrl } from '../utils/config';
import { getActionErrorMessage } from '../utils/errorHandler';
import { buildCurriculumDefaultsFromRank, findRank, preceptorServesRank } from '../utils/rankUtils';
import LoadingSpinner from './LoadingSpinner';
import FailurePage from './FailurePage.jsx';
import CurriculumFormsPanel from './CurriculumFormsPanel';
import '../styles/ManageCurriculums.css';

const token = () => localStorage.getItem('access_token') || '';

const toDateInput = (value) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
};

const toApiDateTime = (value) => {
  if (!value) return '';
  const normalized = value.includes('T') ? value : `${value}T00:00:00`;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString();
};

const toDateTimeInput = (value) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 16);
};

const parseCsv = (value) =>
  String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

const extractId = (item) => {
  if (!item) return '';
  if (typeof item === 'string') return item;
  return item._id || item.id || '';
};

const formatName = (item) => {
  if (!item) return 'Unknown';
  const first = item.firstname || item.name || '';
  const last = item.lastname || '';
  const full = `${first} ${last}`.trim();
  return full || item.email || item._id || 'Unknown';
};

const buildEmptyForm = () => ({
  form_template_id: '',
  schedule: {
    type: 'one_time',
    start_date: '',
    end_date: '',
    repeat_every_days: ''
  },
  target_type: 'form_type',
  target_user_ids: [],
  target_filters_text: {
    account_types: '',
    ranks: '',
    specialities: '',
    institutions: ''
  },
  is_active: true
});

const buildEmptyDraft = () => ({
  name: '',
  start_date: '',
  end_date: '',
  rankId: '',
  student_ids: [],
  preceptor_ids: [],
  forms: []
});

const buildFormDraft = (form = {}) => ({
  form_template_id: form.form_template_id || '',
  schedule: {
    type: form.schedule?.type || 'one_time',
    start_date: toDateTimeInput(form.schedule?.start_date),
    end_date: toDateTimeInput(form.schedule?.end_date),
    repeat_every_days:
      form.schedule?.repeat_every_days === null || form.schedule?.repeat_every_days === undefined
        ? ''
        : String(form.schedule.repeat_every_days)
  },
  target_type: form.target_type || 'form_type',
  target_user_ids: Array.isArray(form.target_user_ids) ? form.target_user_ids.map(extractId).filter(Boolean) : [],
  target_filters_text: {
    account_types: Array.isArray(form.target_filters?.account_types) ? form.target_filters.account_types.join(', ') : '',
    ranks: Array.isArray(form.target_filters?.ranks) ? form.target_filters.ranks.join(', ') : '',
    specialities: Array.isArray(form.target_filters?.specialities) ? form.target_filters.specialities.join(', ') : '',
    institutions: Array.isArray(form.target_filters?.institutions) ? form.target_filters.institutions.join(', ') : ''
  },
  is_active: form.is_active !== false
});

const buildCurriculumDraft = (curriculum) => ({
  ...buildEmptyDraft(),
  name: curriculum?.name || '',
  start_date: toDateInput(curriculum?.start_date),
  end_date: toDateInput(curriculum?.end_date),
  // Legacy curricula store only the rank name; findRank resolves either form.
  rankId: curriculum?.rankId || curriculum?.rank || '',
  student_ids: Array.isArray(curriculum?.students) ? curriculum.students.map(extractId).filter(Boolean) : [],
  preceptor_ids: Array.isArray(curriculum?.preceptors) ? curriculum.preceptors.map(extractId).filter(Boolean) : [],
  forms: Array.isArray(curriculum?.forms) ? curriculum.forms.map(buildFormDraft) : []
});

const normalizeList = (items = []) =>
  normalizeUsers(Array.isArray(items) ? items : []).map((item) => ({
    ...item,
    _id: item._id || item.id || ''
  }));

const buildFormPayload = (form) => ({
  form_template_id: form.form_template_id,
  schedule: {
    type: form.schedule.type,
    start_date: form.schedule.start_date,
    end_date: form.schedule.end_date,
    repeat_every_days: form.schedule.type === 'recurring' && form.schedule.repeat_every_days !== ''
      ? Number(form.schedule.repeat_every_days)
      : null
  },
  target_type: form.target_type,
  target_user_ids: form.target_type === 'explicit' ? form.target_user_ids.filter(Boolean) : [],
  target_filters: form.target_type === 'filters'
    ? {
        account_types: parseCsv(form.target_filters_text.account_types),
        ranks: parseCsv(form.target_filters_text.ranks),
        specialities: parseCsv(form.target_filters_text.specialities),
        institutions: parseCsv(form.target_filters_text.institutions)
      }
    : null,
  is_active: Boolean(form.is_active),
  created_at: form.created_at,
  updated_at: new Date().toISOString()
});

const requestJson = async (url, options = {}) => {
  const response = await fetch(url, {
    ...options,
    headers: {
      Authorization: `Bearer ${token()}`,
      'Content-Type': 'application/json',
      ...(options.headers || {})
    }
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Request failed with status ${response.status}`);
  }

  return response.json();
};

const requestText = async (url, options = {}) => {
  const response = await fetch(url, {
    ...options,
    headers: {
      Authorization: `Bearer ${token()}`,
      ...(options.headers || {})
    }
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Request failed with status ${response.status}`);
  }

  return response.text();
};

const appendList = (params, key, values = []) => {
  values.forEach((value) => params.append(key, value));
};

const CurriculumMemberPicker = ({
  title,
  icon,
  items,
  selectedIds,
  onChange,
  loading,
  accentClass = 'mc-accent-blue',
  emptyLabel = 'No items found'
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const filteredItems = useMemo(() => {
    const search = searchTerm.toLowerCase().trim();
    if (!search) return items;
    return items.filter((item) => {
      const name = formatName(item).toLowerCase();
      const id = String(item._id || '').toLowerCase();
      const email = String(item.email || '').toLowerCase();
      return name.includes(search) || id.includes(search) || email.includes(search);
    });
  }, [items, searchTerm]);

  const toggleId = (id) => {
    onChange(
      selectedIds.includes(id)
        ? selectedIds.filter((existing) => existing !== id)
        : [...selectedIds, id]
    );
  };

  const toggleAll = () => {
    if (filteredItems.length === 0) return;
    const filteredIds = filteredItems.map((item) => item._id);
    const allSelected = filteredIds.every((id) => selectedIds.includes(id));
    onChange(allSelected ? selectedIds.filter((id) => !filteredIds.includes(id)) : Array.from(new Set([...selectedIds, ...filteredIds])));
  };

  const selectedItems = selectedIds
    .map((id) => items.find((item) => item._id === id))
    .filter(Boolean);

  return (
    <section className="mc-picker-panel">
      <div className="mc-section-head mc-section-head--compact">
        <div className="mc-section-title-row">
          <div className={`mc-section-icon ${accentClass}`}>{icon}</div>
          <div>
            <h4 className="mc-section-title">{title}</h4>
            <p className="mc-section-subtitle">{selectedIds.length} selected</p>
          </div>
        </div>
        <button type="button" className="mc-ghost-btn" onClick={toggleAll} disabled={!filteredItems.length}>
          {filteredItems.every((item) => selectedIds.includes(item._id)) ? 'Clear filtered' : 'Select filtered'}
        </button>
      </div>

      <div className="mc-chip-cloud">
        {selectedItems.length === 0 ? (
          <div className="mc-empty-inline">{title} will appear here as you select them.</div>
        ) : (
          selectedItems.map((item) => (
            <span className="mc-chip" key={item._id}>
              {formatName(item)}
              <button type="button" onClick={() => toggleId(item._id)} aria-label={`Remove ${formatName(item)}`}>
                <FaTimes />
              </button>
            </span>
          ))
        )}
      </div>

      <div className="mc-search-box mc-search-box--soft">
        <FaSearch />
        <input
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={`Search ${title.toLowerCase()}...`}
        />
      </div>

      <div className="mc-select-list">
        {loading ? (
          <div className="mc-list-loading">
            <LoadingSpinner text={`Loading ${title.toLowerCase()}...`} variant="inline" size="small" />
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="mc-empty-inline mc-empty-inline--soft">{emptyLabel}</div>
        ) : (
          filteredItems.map((item) => {
            const isSelected = selectedIds.includes(item._id);
            return (
              <button
                type="button"
                key={item._id}
                className={`mc-person-row ${isSelected ? 'is-selected' : ''}`}
                onClick={() => toggleId(item._id)}
              >
                <div className="mc-person-row__main">
                  <span className="mc-checkbox">{isSelected ? '✓' : ''}</span>
                  <div>
                    <strong>{formatName(item)}</strong>
                    <span>{item.email || item._id}</span>
                  </div>
                </div>
                {item.rank ? <span className="mc-rank-badge">{item.rank}</span> : null}
              </button>
            );
          })
        )}
      </div>
    </section>
  );
};

const CurriculumEditor = ({
  mode,
  draft,
  students,
  preceptors,
  formTemplates,
  loadingStudents,
  loadingPreceptors,
  loadingTemplates,
  onCancel,
  onSubmit,
  saving,
  selectedCurriculum,
  onDraftChange,
  ranks = []
}) => {
  const [expandedFormIndex, setExpandedFormIndex] = useState(0);
  const [newForm, setNewForm] = useState(buildEmptyForm());

  useEffect(() => {
    setExpandedFormIndex(0);
    setNewForm(buildEmptyForm());
  }, [selectedCurriculum?._id, mode]);

  const curriculumMembers = useMemo(() => [...students, ...preceptors], [students, preceptors]);

  const selectedRank = findRank(ranks, draft.rankId);

  // Only offer users eligible for the selected rank: preceptors must list it in
  // rankIds, students must carry its name (unranked students stay visible).
  const rankEligibleStudents = useMemo(() => {
    if (!selectedRank) return students;
    return students.filter((student) => {
      const rank = (student.rank || '').toLowerCase();
      return !rank || rank === selectedRank.name.toLowerCase();
    });
  }, [students, selectedRank]);

  const rankEligiblePreceptors = useMemo(() => {
    if (!draft.rankId) return preceptors;
    return preceptors.filter((preceptor) => preceptorServesRank(preceptor, draft.rankId, ranks));
  }, [preceptors, draft.rankId, ranks]);

  const updateField = (field, value) => {
    onDraftChange({ ...draft, [field]: value });
  };

  const updateForm = (index, patch) => {
    const nextForms = draft.forms.map((form, formIndex) => (formIndex === index ? { ...form, ...patch } : form));
    onDraftChange({ ...draft, forms: nextForms });
  };

  const removeForm = (index) => {
    onDraftChange({ ...draft, forms: draft.forms.filter((_, formIndex) => formIndex !== index) });
    setExpandedFormIndex((current) => Math.max(0, current - 1));
  };

  const addForm = () => {
    if (!newForm.form_template_id) {
      return;
    }
    onDraftChange({ ...draft, forms: [...draft.forms, buildFormDraft(newForm)] });
    setNewForm(buildEmptyForm());
    setExpandedFormIndex(draft.forms.length);
  };

  const save = async (event) => {
    event.preventDefault();
    await onSubmit({ ...draft, forms: draft.forms.map(buildFormPayload) });
  };

  return (
    <form className="mc-editor-shell" onSubmit={save}>
      <div className="mc-editor-head">
        <div>
          <p className="mc-eyebrow">{mode === 'create' ? 'Create curriculum' : 'Edit curriculum'}</p>
          <h3>{mode === 'create' ? 'Build a new curriculum' : `Working on ${selectedCurriculum?.name || 'selected curriculum'}`}</h3>
          <p>
            Update the core curriculum details, manage members inline, and keep form assignments in the same place.
          </p>
        </div>
        <div className="mc-editor-actions">
          <button type="button" className="mc-ghost-btn" onClick={onCancel}>
            <FaArrowLeft />
            Back
          </button>
          <button type="submit" className="mc-primary-btn" disabled={saving}>
            {saving ? <FaSpinner className="mc-spin" /> : <FaSave />}
            {saving ? 'Saving...' : 'Save curriculum'}
          </button>
        </div>
      </div>

      <div className="mc-notice-grid">
        <div className="mc-form-card">
          <div className="mc-section-head mc-section-head--compact">
            <div className="mc-section-title-row">
              <div className="mc-section-icon mc-accent-blue"><FaBook /></div>
              <div>
                <h4 className="mc-section-title">Core details</h4>
                <p className="mc-section-subtitle">Name, dates, and rank.</p>
              </div>
            </div>
          </div>

          <div className="mc-grid-2">
            <label className="mc-field">
              <span>Name</span>
              <input value={draft.name} onChange={(e) => updateField('name', e.target.value)} placeholder="Curriculum name" required />
            </label>
            <label className="mc-field">
              <span>Rank</span>
              <select
                value={draft.rankId}
                onChange={(e) => {
                  const rankId = e.target.value;
                  const rank = findRank(ranks, rankId);
                  // Adopt the rank's rotation defaults when creating a new curriculum
                  // and no dates have been entered yet.
                  const defaults = rank ? buildCurriculumDefaultsFromRank(rank) : null;
                  onDraftChange((prev) => ({
                    ...prev,
                    rankId,
                    start_date: defaults?.hasDates && !prev.start_date ? defaults.startDate : prev.start_date,
                    end_date: defaults?.hasDates && !prev.end_date ? defaults.endDate : prev.end_date
                  }));
                }}
                required
              >
                <option value="">Select a rank…</option>
                {ranks.map((rank) => (
                  <option key={rank._id} value={rank._id}>
                    {rank.name} — matched by {rank.matchBy}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="mc-grid-2">
            <label className="mc-field">
              <span>Start date</span>
              <input type="date" value={draft.start_date} onChange={(e) => updateField('start_date', e.target.value)} required />
            </label>
            <label className="mc-field">
              <span>End date</span>
              <input type="date" value={draft.end_date} onChange={(e) => updateField('end_date', e.target.value)} required />
            </label>
          </div>
        </div>

        <div className="mc-form-card mc-form-card--members">
          <div className="mc-section-head mc-section-head--compact">
            <div className="mc-section-title-row">
              <div className="mc-section-icon mc-accent-blue"><FaUsers /></div>
              <div>
                <h4 className="mc-section-title">Members</h4>
                <p className="mc-section-subtitle">Pick students and preceptors inline.</p>
              </div>
            </div>
          </div>

          <div className="mc-stack">
            <CurriculumMemberPicker
              title="Students"
              icon={<FaGraduationCap />}
              items={rankEligibleStudents}
              selectedIds={draft.student_ids}
              onChange={(student_ids) => onDraftChange({ ...draft, student_ids })}
              loading={loadingStudents}
              accentClass="mc-accent-blue"
              emptyLabel="No students available for this rank"
            />

            <CurriculumMemberPicker
              title="Preceptors"
              icon={<FaUserTie />}
              items={rankEligiblePreceptors}
              selectedIds={draft.preceptor_ids}
              onChange={(preceptor_ids) => onDraftChange({ ...draft, preceptor_ids })}
              loading={loadingPreceptors}
              accentClass="mc-accent-navy"
              emptyLabel="No preceptors available for this rank"
            />
          </div>
        </div>
      </div>

      <div className="mc-form-card mc-form-card--forms">
        <div className="mc-section-head">
          <div className="mc-section-title-row">
            <div className="mc-section-icon mc-accent-gold"><FaLayerGroup /></div>
            <div>
              <h4 className="mc-section-title">Linked forms</h4>
              <p className="mc-section-subtitle">Add and edit form schedules without leaving the page.</p>
            </div>
          </div>
          <div className="mc-counter-pill">{draft.forms.length} forms</div>
        </div>

        <div className="mc-form-list">
          {draft.forms.length === 0 ? (
            <div className="mc-empty-inline mc-empty-inline--soft">
              No forms are linked yet. Use the builder below to attach the first one.
            </div>
          ) : (
            draft.forms.map((form, index) => {
              const template = formTemplates.find((candidate) => candidate._id === form.form_template_id);
              const isExpanded = expandedFormIndex === index;
              return (
                <article className={`mc-form-item ${isExpanded ? 'is-open' : ''}`} key={`${form.form_template_id || 'form'}-${index}`}>
                  <button type="button" className="mc-form-item__summary" onClick={() => setExpandedFormIndex(isExpanded ? -1 : index)}>
                    <div>
                      <strong>{template?.name || 'Untitled form'}</strong>
                      <span>{form.schedule.type === 'recurring' ? 'Recurring schedule' : 'One-time schedule'}</span>
                    </div>
                    <div className="mc-summary-badges">
                      <span className={`mc-status-pill ${form.is_active ? 'is-active' : 'is-off'}`}>
                        {form.is_active ? 'Active' : 'Inactive'}
                      </span>
                      <span className="mc-status-pill mc-status-pill--soft">{form.target_type}</span>
                    </div>
                  </button>

                  {isExpanded ? (
                    <div className="mc-form-item__body">
                      <div className="mc-grid-2">
                        <label className="mc-field">
                          <span>Template</span>
                          <select
                            value={form.form_template_id}
                            onChange={(e) => updateForm(index, { form_template_id: e.target.value })}
                            required
                          >
                            <option value="">Select template</option>
                            {formTemplates.map((templateOption) => (
                              <option key={templateOption._id} value={templateOption._id}>
                                {templateOption.name}
                              </option>
                            ))}
                          </select>
                        </label>

                        <label className="mc-field">
                          <span>Form status</span>
                          <select
                            value={form.is_active ? 'active' : 'inactive'}
                            onChange={(e) => updateForm(index, { is_active: e.target.value === 'active' })}
                          >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </select>
                        </label>
                      </div>

                      <div className="mc-grid-2">
                        <label className="mc-field">
                          <span>Schedule type</span>
                          <select
                            value={form.schedule.type}
                            onChange={(e) => updateForm(index, {
                              schedule: {
                                ...form.schedule,
                                type: e.target.value,
                                repeat_every_days: e.target.value === 'recurring' ? form.schedule.repeat_every_days : ''
                              }
                            })}
                          >
                            <option value="one_time">One time</option>
                            <option value="recurring">Recurring</option>
                          </select>
                        </label>

                        {form.schedule.type === 'recurring' ? (
                          <label className="mc-field">
                            <span>Repeat every (days)</span>
                            <input
                              type="number"
                              min="1"
                              value={form.schedule.repeat_every_days}
                              onChange={(e) => updateForm(index, {
                                schedule: { ...form.schedule, repeat_every_days: e.target.value }
                              })}
                              placeholder="7"
                            />
                          </label>
                        ) : (
                          <div />
                        )}
                      </div>

                      <div className="mc-grid-2">
                        <label className="mc-field">
                          <span>Start</span>
                          <input
                            type="datetime-local"
                            value={form.schedule.start_date}
                            onChange={(e) => updateForm(index, {
                              schedule: { ...form.schedule, start_date: e.target.value }
                            })}
                          />
                        </label>
                        <label className="mc-field">
                          <span>End</span>
                          <input
                            type="datetime-local"
                            value={form.schedule.end_date}
                            onChange={(e) => updateForm(index, {
                              schedule: { ...form.schedule, end_date: e.target.value }
                            })}
                          />
                        </label>
                      </div>

                      <div className="mc-grid-2">
                        <label className="mc-field">
                          <span>Target type</span>
                          <select
                            value={form.target_type}
                            onChange={(e) => updateForm(index, { target_type: e.target.value })}
                          >
                            <option value="form_type">By form type</option>
                            <option value="explicit">Explicit users</option>
                            <option value="filters">Curriculum filters</option>
                          </select>
                        </label>
                        <div className="mc-field mc-field--ghost">
                          <span>Audience</span>
                          <p>
                            {form.target_type === 'explicit'
                              ? 'Choose specific members.'
                              : form.target_type === 'filters'
                                ? 'Use comma-separated filter values.'
                                : 'Let the backend infer audience from the form type.'}
                          </p>
                        </div>
                      </div>

                      {form.target_type === 'explicit' ? (
                        <div className="mc-mini-picker-wrap">
                          <CurriculumMemberPicker
                            title="Form audience"
                            icon={<FaFilter />}
                            items={curriculumMembers}
                            selectedIds={form.target_user_ids}
                            onChange={(target_user_ids) => updateForm(index, { target_user_ids })}
                            loading={loadingStudents || loadingPreceptors}
                            accentClass="mc-accent-gold"
                            emptyLabel="No curriculum members available"
                          />
                        </div>
                      ) : null}

                      {form.target_type === 'filters' ? (
                        <div className="mc-grid-2 mc-grid-2--filters">
                          <label className="mc-field">
                            <span>Account types</span>
                            <input
                              value={form.target_filters_text.account_types}
                              onChange={(e) => updateForm(index, {
                                target_filters_text: { ...form.target_filters_text, account_types: e.target.value }
                              })}
                              placeholder="student, preceptor"
                            />
                          </label>
                          <label className="mc-field">
                            <span>Ranks</span>
                            <input
                              value={form.target_filters_text.ranks}
                              onChange={(e) => updateForm(index, {
                                target_filters_text: { ...form.target_filters_text, ranks: e.target.value }
                              })}
                              placeholder="PharmD, SPEP"
                            />
                          </label>
                          <label className="mc-field">
                            <span>Specialities</span>
                            <input
                              value={form.target_filters_text.specialities}
                              onChange={(e) => updateForm(index, {
                                target_filters_text: { ...form.target_filters_text, specialities: e.target.value }
                              })}
                              placeholder="cardiology, pediatrics"
                            />
                          </label>
                          <label className="mc-field">
                            <span>Institutions</span>
                            <input
                              value={form.target_filters_text.institutions}
                              onChange={(e) => updateForm(index, {
                                target_filters_text: { ...form.target_filters_text, institutions: e.target.value }
                              })}
                              placeholder="hospital-a, hospital-b"
                            />
                          </label>
                        </div>
                      ) : null}

                      <div className="mc-inline-actions">
                        <button type="button" className="mc-danger-btn" onClick={() => removeForm(index)}>
                          <FaTrash /> Remove form
                        </button>
                      </div>
                    </div>
                  ) : null}
                </article>
              );
            })
          )}
        </div>

        <div className="mc-add-form-grid">
          <div className="mc-add-form-title">
            <FaPlus /> Add another form
          </div>
          <div className="mc-grid-2">
            <label className="mc-field">
              <span>Template</span>
              <select value={newForm.form_template_id} onChange={(e) => setNewForm({ ...newForm, form_template_id: e.target.value })}>
                <option value="">Select template</option>
                {formTemplates.map((templateOption) => (
                  <option key={templateOption._id} value={templateOption._id}>
                    {templateOption.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="mc-field">
              <span>Schedule type</span>
              <select
                value={newForm.schedule.type}
                onChange={(e) => setNewForm({
                  ...newForm,
                  schedule: {
                    ...newForm.schedule,
                    type: e.target.value,
                    repeat_every_days: e.target.value === 'recurring' ? newForm.schedule.repeat_every_days : ''
                  }
                })}
              >
                <option value="one_time">One time</option>
                <option value="recurring">Recurring</option>
              </select>
            </label>
          </div>
          <div className="mc-grid-2">
            <label className="mc-field">
              <span>Start</span>
              <input type="datetime-local" value={newForm.schedule.start_date} onChange={(e) => setNewForm({ ...newForm, schedule: { ...newForm.schedule, start_date: e.target.value } })} />
            </label>
            <label className="mc-field">
              <span>End</span>
              <input type="datetime-local" value={newForm.schedule.end_date} onChange={(e) => setNewForm({ ...newForm, schedule: { ...newForm.schedule, end_date: e.target.value } })} />
            </label>
          </div>
          {newForm.schedule.type === 'recurring' ? (
            <label className="mc-field">
              <span>Repeat every (days)</span>
              <input
                type="number"
                min="1"
                value={newForm.schedule.repeat_every_days}
                onChange={(e) => setNewForm({ ...newForm, schedule: { ...newForm.schedule, repeat_every_days: e.target.value } })}
                placeholder="7"
              />
            </label>
          ) : null}
          <div className="mc-inline-actions mc-inline-actions--spread">
            <label className="mc-toggle">
              <input
                type="checkbox"
                checked={newForm.is_active}
                onChange={(e) => setNewForm({ ...newForm, is_active: e.target.checked })}
              />
              <span>Active</span>
            </label>
            <button type="button" className="mc-secondary-btn" onClick={addForm} disabled={!newForm.form_template_id}>
              <FaPlus /> Add form
            </button>
          </div>
        </div>
      </div>
    </form>
  );
};

const ManageCurriculumsWorkspace = () => {
  const currentUserRole = getCurrentUserRole();
  const [curricula, setCurricula] = useState([]);
  const [students, setStudents] = useState([]);
  const [preceptors, setPreceptors] = useState([]);
  const [formTemplates, setFormTemplates] = useState([]);
  const [ranks, setRanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [preceptorsLoading, setPreceptorsLoading] = useState(false);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [mode, setMode] = useState('view');
  const [selectedCurriculumId, setSelectedCurriculumId] = useState('');
  const [draft, setDraft] = useState(buildEmptyDraft());
  const [notice, setNotice] = useState({ type: '', message: '' });
  const [pageError, setPageError] = useState('');

  const selectedCurriculum = useMemo(
    () => curricula.find((curriculum) => curriculum._id === selectedCurriculumId) || null,
    [curricula, selectedCurriculumId]
  );

  const curriculumMembers = useMemo(() => [...students, ...preceptors], [students, preceptors]);

  const filteredCurricula = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    return curricula.filter((curriculum) => {
      if (!term) return true;
      const haystack = [curriculum.name, curriculum.rank, curriculum._id]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [curricula, searchTerm]);

  const showNotice = (type, message) => {
    setNotice({ type, message });
    if (type === 'success') {
      setTimeout(() => setNotice({ type: '', message: '' }), 3500);
    }
  };

  const loadCurricula = useCallback(async () => {
    const response = await fetch(getApiUrl('curriculum/curriculums'), {
      headers: { Authorization: `Bearer ${token()}` }
    });
    if (!response.ok) throw new Error('Failed to fetch curricula');
    return response.json();
  }, []);

  const loadStudents = useCallback(async () => {
    setStudentsLoading(true);
    try {
      const response = await fetch(getApiUrl('crud/get_all_students_basic_info/'), {
        headers: { Authorization: `Bearer ${token()}` }
      });
      if (!response.ok) throw new Error('Failed to fetch students');
      const data = await response.json();
      setStudents(normalizeList(data));
    } finally {
      setStudentsLoading(false);
    }
  }, []);

  const loadPreceptors = useCallback(async () => {
    setPreceptorsLoading(true);
    try {
      const response = await fetch(getApiUrl('crud/get_all_preceptors_basic_info/'), {
        headers: { Authorization: `Bearer ${token()}` }
      });
      if (!response.ok) throw new Error('Failed to fetch preceptors');
      const data = await response.json();
      setPreceptors(normalizeList(data));
    } finally {
      setPreceptorsLoading(false);
    }
  }, []);

  const loadFormTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    try {
      const response = await fetch(getApiUrl('form-builder/templates/'), {
        headers: { Authorization: `Bearer ${token()}` }
      });
      if (!response.ok) throw new Error('Failed to fetch form templates');
      const data = await response.json();
      setFormTemplates((Array.isArray(data) ? data : []).filter((template) => template.is_active && template.is_published));
    } finally {
      setTemplatesLoading(false);
    }
  }, []);

  const loadRanks = useCallback(async () => {
    try {
      const response = await fetch(getApiUrl('ranks/ranks'), {
        headers: { Authorization: `Bearer ${token()}` }
      });
      if (!response.ok) throw new Error('Failed to fetch ranks');
      const data = await response.json();
      setRanks(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading ranks:', error);
    }
  }, []);

  const reloadAll = useCallback(async () => {
    try {
      setLoading(true);
      setPageError('');
      const [curriculaData] = await Promise.all([
        loadCurricula(),
        loadStudents(),
        loadPreceptors(),
        loadFormTemplates(),
        loadRanks()
      ]);
      setCurricula(Array.isArray(curriculaData) ? curriculaData : []);
      if (!selectedCurriculumId && Array.isArray(curriculaData) && curriculaData.length > 0) {
        setSelectedCurriculumId(curriculaData[0]._id);
      }
    } catch (error) {
      setPageError(getActionErrorMessage(error, 'load curriculums'));
    } finally {
      setLoading(false);
    }
  }, [loadCurricula, loadFormTemplates, loadPreceptors, loadStudents, loadRanks]);

  useEffect(() => {
    reloadAll();
  }, [reloadAll]);

  useEffect(() => {
    if (selectedCurriculum && mode === 'view') {
      setDraft(buildCurriculumDraft(selectedCurriculum));
    }
  }, [selectedCurriculum, mode]);

  const openCreate = () => {
    setMode('create');
    setSelectedCurriculumId('');
    setDraft(buildEmptyDraft());
    setNotice({ type: '', message: '' });
  };

  const openEdit = (curriculum) => {
    setMode('edit');
    setSelectedCurriculumId(curriculum._id);
    setDraft(buildCurriculumDraft(curriculum));
    setNotice({ type: '', message: '' });
  };

  const cancelEditor = () => {
    if (selectedCurriculum) {
      setMode('view');
      setDraft(buildCurriculumDraft(selectedCurriculum));
    } else {
      setMode('view');
      setDraft(buildEmptyDraft());
    }
  };

  const createCurriculum = async (nextDraft) => {
    const params = new URLSearchParams();
    params.set('name', nextDraft.name);
    params.set('start_date', toApiDateTime(nextDraft.start_date));
    params.set('end_date', toApiDateTime(nextDraft.end_date));
    params.set('rank_id', nextDraft.rankId);
    appendList(params, 'student_ids', nextDraft.student_ids);
    appendList(params, 'preceptor_ids', nextDraft.preceptor_ids);

    const response = await fetch(getApiUrl(`curriculum/create_curriculum?${params.toString()}`), {
      method: 'POST',
      headers: { Authorization: `Bearer ${token()}` }
    });

    if (!response.ok) {
      throw new Error(await response.text());
    }

    return response.json();
  };

  const updateCurriculum = async (nextDraft) => {
    if (!selectedCurriculum) {
      throw new Error('No curriculum selected');
    }

    const params = new URLSearchParams();
    if (nextDraft.name !== selectedCurriculum.name) params.set('name', nextDraft.name);
    if (nextDraft.start_date !== toDateInput(selectedCurriculum.start_date)) {
      params.set('start_date', toApiDateTime(nextDraft.start_date));
    }
    if (nextDraft.end_date !== toDateInput(selectedCurriculum.end_date)) {
      params.set('end_date', toApiDateTime(nextDraft.end_date));
    }
    if (nextDraft.rankId !== (selectedCurriculum.rankId || selectedCurriculum.rank)) {
      params.set('rank_id', nextDraft.rankId);
    }

    const nextForms = Array.isArray(nextDraft.forms) ? nextDraft.forms : [];
    const currentForms = buildCurriculumDraft(selectedCurriculum).forms;
    const formsChanged = JSON.stringify(nextForms) !== JSON.stringify(currentForms.map(buildFormPayload));

    if (formsChanged) {
      if (!params.toString()) {
        params.set('name', selectedCurriculum.name || selectedCurriculum._id);
      }
    }

    if (params.toString()) {
      const response = await fetch(getApiUrl(`curriculum/update_curriculum/?curriculum_id=${encodeURIComponent(selectedCurriculum._id)}&${params.toString()}`), {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token()}`,
          'Content-Type': 'application/json'
        },
        body: formsChanged ? JSON.stringify(nextForms) : undefined
      });

      if (!response.ok) {
        throw new Error(await response.text());
      }
    }
  };

  const syncUsers = async (nextDraft) => {
    if (!selectedCurriculum) return;

    const currentStudentIds = normalizeArray(selectedCurriculum.students);
    const currentPreceptorIds = normalizeArray(selectedCurriculum.preceptors);
    const addedStudents = nextDraft.student_ids.filter((id) => !currentStudentIds.includes(id));
    const removedStudents = currentStudentIds.filter((id) => !nextDraft.student_ids.includes(id));
    const addedPreceptors = nextDraft.preceptor_ids.filter((id) => !currentPreceptorIds.includes(id));
    const removedPreceptors = currentPreceptorIds.filter((id) => !nextDraft.preceptor_ids.includes(id));

    if (addedStudents.length || addedPreceptors.length) {
      const response = await requestJson(getApiUrl(`curriculum/${encodeURIComponent(selectedCurriculum._id)}/add_users`), {
        method: 'POST',
        body: JSON.stringify({
          ...(addedStudents.length ? { student_ids: addedStudents } : {}),
          ...(addedPreceptors.length ? { preceptor_ids: addedPreceptors } : {})
        })
      });
      if (!response) {
        throw new Error('Failed to add users');
      }
    }

    if (removedStudents.length || removedPreceptors.length) {
      const params = new URLSearchParams();
      appendList(params, 'student_ids', removedStudents);
      appendList(params, 'preceptor_ids', removedPreceptors);
      await requestText(getApiUrl(`curriculum/${encodeURIComponent(selectedCurriculum._id)}/remove_users?${params.toString()}`), {
        method: 'DELETE'
      });
    }
  };

  const handleSubmit = async (nextDraft) => {
    if (!nextDraft.name.trim()) {
      showNotice('error', 'Curriculum name is required.');
      return;
    }

    if (!nextDraft.start_date || !nextDraft.end_date) {
      showNotice('error', 'Start and end dates are required.');
      return;
    }

    try {
      setSaving(true);
      setPageError('');

      const normalizedDraft = {
        ...nextDraft,
        student_ids: Array.from(new Set(nextDraft.student_ids)),
        preceptor_ids: Array.from(new Set(nextDraft.preceptor_ids))
      };

      if (mode === 'create') {
        await createCurriculum(normalizedDraft);
        showNotice('success', 'Curriculum created successfully.');
      } else {
        await syncUsers(normalizedDraft);
        await updateCurriculum(normalizedDraft);
        showNotice('success', 'Curriculum updated successfully.');
      }

      await reloadAll();
      if (mode === 'create') {
        setMode('view');
      }
    } catch (error) {
      const message = getActionErrorMessage(error, mode === 'create' ? 'create curriculum' : 'update curriculum');
      setPageError(message);
      showNotice('error', message);
    } finally {
      setSaving(false);
    }
  };

  const selectedStats = useMemo(() => {
    const totalForms = selectedCurriculum?.forms?.length || 0;
    return {
      students: selectedCurriculum?.students?.length || 0,
      preceptors: selectedCurriculum?.preceptors?.length || 0,
      forms: totalForms
    };
  }, [selectedCurriculum]);

  if (pageError) {
    return (
      <div className="mc-page">
        <div className="mc-shell">
          <FailurePage
            title="Oops, curriculums failed to load"
            message={pageError}
            actionLabel="Retry"
            onAction={reloadAll}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="mc-page">
      <div className="mc-shell">
      <section className="mc-hero">
        <div className="mc-hero__copy">
          <div className="mc-hero__eyebrow">
            <FaStethoscope /> Curriculum studio
          </div>
          <h1>Manage curriculums without popups.</h1>
          <p>
            Create, edit, and assign members in a single flow with the same clean blue glass theme used across the secure auth pages.
          </p>
        </div>

        <div className="mc-hero__stats">
          <div className="mc-stat">
            <strong>{curricula.length}</strong>
            <span>Curriculums</span>
          </div>
          <div className="mc-stat">
            <strong>{students.length}</strong>
            <span>Students</span>
          </div>
          <div className="mc-stat">
            <strong>{preceptors.length}</strong>
            <span>Preceptors</span>
          </div>
          <div className="mc-stat">
            <strong>{formTemplates.length}</strong>
            <span>Templates</span>
          </div>
        </div>
      </section>

      {notice.message ? (
        <div className={`mc-banner ${notice.type === 'error' ? 'is-error' : 'is-success'}`} data-toast-inline data-toast-suppress={notice.type === 'error' ? 'true' : undefined}>
          {notice.type === 'error' ? <FaExclamationTriangle /> : <FaCheckCircle />}
          <span>{notice.message}</span>
        </div>
      ) : null}

      <div className="mc-toolbar">
        <div className="mc-search-box">
          <FaSearch />
          <input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search curriculums by name, rank, or ID"
          />
        </div>

        <div className="mc-toolbar-actions">
          <button type="button" className="mc-secondary-btn" onClick={reloadAll} disabled={loading}>
            {loading ? <FaSpinner className="mc-spin" /> : <FaLayerGroup />}
            Refresh
          </button>
          <button type="button" className="mc-primary-btn" onClick={openCreate}>
            <FaPlus /> New curriculum
          </button>
        </div>
      </div>

      {loading ? (
        <div className="mc-loading-shell">
          <LoadingSpinner text="Loading curriculums..." variant="inline" size="small" />
        </div>
      ) : (
        <div className="mc-layout">
          <aside className="mc-list-panel">
            <div className="mc-section-head">
              <div>
                <h3 className="mc-panel-title">Curriculum list</h3>
                <p className="mc-section-subtitle">Select a curriculum to inspect and update it inline.</p>
              </div>
              <div className="mc-counter-pill">{filteredCurricula.length} shown</div>
            </div>

            <div className="mc-list-stack">
              {filteredCurricula.length === 0 ? (
                <div className="mc-empty-card">
                  <FaBook />
                  <strong>No curriculums found</strong>
                  <span>Try a different search term or create a new curriculum.</span>
                </div>
              ) : (
                filteredCurricula.map((curriculum) => {
                  const active = curriculum._id === selectedCurriculumId && mode !== 'create';
                  return (
                    <button
                      type="button"
                      key={curriculum._id}
                      className={`mc-curriculum-card ${active ? 'is-active' : ''}`}
                      onClick={() => {
                        setSelectedCurriculumId(curriculum._id);
                        setMode('view');
                        setDraft(buildCurriculumDraft(curriculum));
                      }}
                    >
                      <div className="mc-curriculum-card__top">
                        <div>
                          <h4>{curriculum.name || curriculum._id}</h4>
                          <p>{curriculum.rank || 'No rank set'}</p>
                        </div>
                        <span className="mc-status-pill">{curriculum.forms?.length || 0} forms</span>
                      </div>

                      <div className="mc-curriculum-card__meta">
                        <span><FaCalendarAlt /> {toDateInput(curriculum.start_date)} - {toDateInput(curriculum.end_date)}</span>
                        <span><FaUsers /> {(curriculum.students || []).length} students</span>
                        <span><FaUserTie /> {(curriculum.preceptors || []).length} preceptors</span>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </aside>

          <main className="mc-editor-panel">
            {mode === 'create' ? (
              <CurriculumEditor
                mode="create"
                draft={draft}
                students={students}
                preceptors={preceptors}
                formTemplates={formTemplates}
                loadingStudents={studentsLoading}
                loadingPreceptors={preceptorsLoading}
                loadingTemplates={templatesLoading}
                onCancel={cancelEditor}
                onSubmit={handleSubmit}
                saving={saving}
                selectedCurriculum={null}
                onDraftChange={setDraft}
                ranks={ranks}
              />
            ) : selectedCurriculum ? (
              <>
                <div className="mc-detail-hero">
                  <div>
                    <p className="mc-eyebrow">Selected curriculum</p>
                    <h2>{selectedCurriculum.name || selectedCurriculum._id}</h2>
                    <p>
                      {selectedCurriculum.rank || 'No rank set'} · {toDateInput(selectedCurriculum.start_date)} to {toDateInput(selectedCurriculum.end_date)}
                    </p>
                  </div>
                  <div className="mc-detail-actions">
                    <button type="button" className="mc-secondary-btn" onClick={openCreate}>
                      <FaPlus /> Create new
                    </button>
                    <button type="button" className="mc-primary-btn" onClick={() => openEdit(selectedCurriculum)}>
                      <FaEdit /> Edit inline
                    </button>
                  </div>
                </div>

                <div className="mc-mini-stats">
                  <div className="mc-mini-stat"><strong>{selectedStats.students}</strong><span>Students</span></div>
                  <div className="mc-mini-stat"><strong>{selectedStats.preceptors}</strong><span>Preceptors</span></div>
                  <div className="mc-mini-stat"><strong>{selectedStats.forms}</strong><span>Forms</span></div>
                </div>

                {mode === 'edit' ? (
                  <CurriculumEditor
                    mode="edit"
                    draft={draft}
                    students={students}
                    preceptors={preceptors}
                    formTemplates={formTemplates}
                    loadingStudents={studentsLoading}
                    loadingPreceptors={preceptorsLoading}
                    loadingTemplates={templatesLoading}
                    onCancel={cancelEditor}
                    onSubmit={handleSubmit}
                    saving={saving}
                    selectedCurriculum={selectedCurriculum}
                    onDraftChange={setDraft}
                    ranks={ranks}
                  />
                ) : (
                  <div className="mc-detail-grid">
                    <section className="mc-detail-card">
                      <div className="mc-section-head mc-section-head--compact">
                        <div className="mc-section-title-row">
                          <div className="mc-section-icon mc-accent-blue"><FaBook /></div>
                          <div>
                            <h4 className="mc-section-title">Overview</h4>
                            <p className="mc-section-subtitle">Quick read-only summary.</p>
                          </div>
                        </div>
                      </div>

                      <div className="mc-overview-list">
                        <div><span>ID</span><strong>{selectedCurriculum._id}</strong></div>
                        <div><span>Name</span><strong>{selectedCurriculum.name || '—'}</strong></div>
                        <div><span>Rank</span><strong>{selectedCurriculum.rank || '—'}</strong></div>
                        <div><span>Dates</span><strong>{toDateInput(selectedCurriculum.start_date)} to {toDateInput(selectedCurriculum.end_date)}</strong></div>
                      </div>
                    </section>

                  </div>
                )}

                {/* Full form linking for the selected curriculum. This replaces a
                    read-only list that showed raw template IDs and offered no way
                    to attach, reschedule, pause or detach a form. */}
                {mode === 'view' && selectedCurriculum && (
                  <CurriculumFormsPanel
                    key={selectedCurriculum._id}
                    curriculumId={selectedCurriculum._id}
                    curriculumName={selectedCurriculum.name}
                  />
                )}
              </>
            ) : (
              <div className="mc-empty-state">
                <FaBook />
                <h3>No curriculum selected</h3>
                <p>Create a curriculum or pick one from the list to start editing inline.</p>
                <button type="button" className="mc-primary-btn" onClick={openCreate}>
                  <FaPlus /> New curriculum
                </button>
              </div>
            )}
          </main>
        </div>
      )}

      {currentUserRole && currentUserRole !== 'admin' && currentUserRole !== 'super_admin' ? (
        <div className="mc-footnote">You are viewing the curriculum studio as {currentUserRole}.</div>
      ) : null}
      </div>
    </div>
  );
};

export default ManageCurriculumsWorkspace;
